import React, { useRef, useEffect, ReactElement, ComponentProps, useCallback, FC } from 'react';
import throttle from 'lodash/throttle';
import times from 'lodash/times';
import styled from 'styled-components';
import { useIsMobile } from '../../utils/getIsMobile';
import { IconForward } from '../icon/IconForward';
import { IconLeft } from '../icon/IconLeft';
import { IconButton } from '../input/IconButton';
import { Box } from '../layout/Box';
import { DataTestId } from '../layout/DataTestIdWrapper';
import { useHoverSlide } from './useHoverSlide';

const CarouselTrackInner = styled.div.withConfig({ displayName: 'CarouselTrackInner' })`
    scroll-snap-type: x mandatory;
    display: flex;
    width: 100%;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }

    & > * {
        min-width: 100%;
        scroll-snap-align: center;
        scroll-snap-stop: always;
        user-select: none;
    }
`;

function scrollTo(element: HTMLElement, left: number, callback: VoidFunction) {
    const onScroll = function () {
        if (Math.abs(element.scrollLeft - left) < 50) {
            element.removeEventListener('scroll', onScroll);
            callback();
        }
    };

    element.addEventListener('scroll', onScroll);
    onScroll();
    element.scrollTo({ left, behavior: 'smooth' });
}

interface CarouselProps extends ComponentProps<typeof Box> {
    count: number;
    activeIndex: number;
    renderItem: (index: number) => ReactElement | null;
    onSlideChange?: (index: number) => void;
    type?: 'buttons' | 'hover';
}

export const Carousel: FC<CarouselProps> = ({
    activeIndex,
    onSlideChange,
    count,
    renderItem,
    type = 'buttons',
    ...other
}) => {
    const scrollingRef = useRef(false);
    const carouselRef = useRef<HTMLDivElement>(null);
    const carouselTrackRef = useRef<HTMLDivElement>(null);
    const isMobile = useIsMobile();
    const isHoverType = type === 'hover';

    const scrollIndex = useCallback(() => {
        if (!carouselRef.current || !carouselTrackRef.current) {
            return 0;
        }

        return Math.round(carouselTrackRef.current.scrollLeft / carouselRef.current.offsetWidth);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [carouselRef.current, carouselTrackRef.current]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onScroll = useCallback(
        throttle(() => {
            if (scrollingRef.current) {
                return;
            }

            const newIndex = scrollIndex();

            if (activeIndex !== newIndex && (!isHoverType || isMobile)) {
                onSlideChange?.(newIndex);
            }
        }, 100),
        [scrollIndex, onSlideChange, activeIndex, scrollingRef.current, isHoverType, isMobile],
    );

    useEffect(() => {
        if (!carouselTrackRef.current || !carouselRef.current) {
            return;
        }

        scrollingRef.current = false;

        const newIndex = scrollIndex();

        if (activeIndex !== newIndex) {
            scrollingRef.current = true;

            scrollTo(
                carouselTrackRef.current,
                carouselRef.current.offsetWidth * activeIndex,
                () => (scrollingRef.current = false),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeIndex, scrollIndex, carouselRef.current, carouselTrackRef.current]);

    useHoverSlide({
        element: carouselRef.current,
        onSlideChange,
        isHoverType,
        slidesCount: count,
        isMobile: Boolean(isMobile),
    });

    const onSlidePrev = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        e.preventDefault();

        if (!scrollingRef.current) {
            onSlideChange?.(activeIndex - 1);
        }
    };

    const onSlideNext = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        e.preventDefault();

        if (!scrollingRef.current) {
            onSlideChange?.(activeIndex + 1);
        }
    };

    const leftArrowEnabled = !isHoverType && activeIndex > 0 && count > 1;
    const rightArrowEnabled = !isHoverType && activeIndex < count - 1 && count > 1;

    return (
        <Box {...other} position="relative" ref={carouselRef}>
            <CarouselTrackInner ref={carouselTrackRef} onScroll={onScroll}>
                {times(count, renderItem)}
            </CarouselTrackInner>
            {leftArrowEnabled ? (
                <Box
                    left={1}
                    top="50%"
                    position="absolute"
                    transform="translateY(-50%)"
                    display={{ xs: 'none', md: 'block' }}
                    dataTestId={DataTestId.SLIDER_LEFT}
                >
                    <IconButton variant="filled" onClick={onSlidePrev}>
                        <IconLeft />
                    </IconButton>
                </Box>
            ) : null}
            {rightArrowEnabled ? (
                <Box
                    right={1}
                    top="50%"
                    position="absolute"
                    transform="translateY(-50%)"
                    display={{ xs: 'none', md: 'block' }}
                    dataTestId={DataTestId.SLIDER_RIGHT}
                >
                    <IconButton variant="filled" onClick={onSlideNext}>
                        <IconForward />
                    </IconButton>
                </Box>
            ) : null}
        </Box>
    );
};
